import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany debe usarse dentro de CompanyProvider');
  }
  return context;
};

const STORAGE_KEY = 'numbers_selected_company_id';

export const CompanyProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [selectedCompany, setSelectedCompanyState] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.role?.name === 'super_admin';

  // Para admin_empresa: usar siempre la empresa del usuario
  const companyForAdmin = user?.company;

  const setSelectedCompany = (company) => {
    if (!isSuperAdmin) return;
    setSelectedCompanyState(company);
    if (company) {
      localStorage.setItem(STORAGE_KEY, company.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    if (!isSuperAdmin) {
      setSelectedCompanyState(companyForAdmin || null);
      setLoading(false);
      return;
    }

    // Super admin: cargar empresas y selección
    const loadCompanies = async () => {
      try {
        const response = await api.get('/companies/selector');
        if (response.data.success && response.data.data?.length > 0) {
          const list = response.data.data;
          setCompanies(list);

          const savedId = localStorage.getItem(STORAGE_KEY);
          const saved = list.find((c) => c.id === savedId);
          if (saved) {
            setSelectedCompanyState(saved);
          } else {
            // Por defecto: primera empresa
            setSelectedCompanyState(list[0]);
            localStorage.setItem(STORAGE_KEY, list[0].id);
          }
        } else {
          setSelectedCompanyState(null);
        }
      } catch (error) {
        console.error('Error cargando empresas:', error);
        setCompanies([]);
        setSelectedCompanyState(null);
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, isSuperAdmin]);

  // Si admin_empresa y cambia el user, actualizar
  useEffect(() => {
    if (!isSuperAdmin && user) {
      setSelectedCompanyState(user?.company || null);
    }
  }, [isSuperAdmin, user]);

  const value = {
    companyId: selectedCompany?.id ?? null,
    company: selectedCompany,
    companies,
    setSelectedCompany,
    isSuperAdmin,
    loading
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};
